import React, { useState, useEffect } from "react";
import {
  Modal,
  Alert,
  Checkbox,
  Form,
  Input,
  Radio,
  Card,
  Divider,
  Steps,
  Select,
  Tag,
  Image,
  Row,
  Col,
  List,
  Avatar,
  DatePicker,
  Dropdown,
  Button,
  Menu,
  Typography,
  Space,
  Badge,
  message,
} from "antd";
import "./VoucherBooking.css";
import { KeyOutlined, MailOutlined, SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import axios from "axios";
import { observer } from "mobx-react-lite";
import { useNavigate, useLocation } from "react-router-dom";

import { useAuth } from "../../../layouts/auth/AuthContext";
import { mstBooking, mstGeneral } from "../../../mobx";
import Navbar from "../../../layouts/navbar";
import { useVoucher } from "./VoucherContext";
import Paragraph from "antd/lib/typography/Paragraph";
import { useCart } from "../../../layouts/cart/CartContext";
import { useReferral } from "../../../utils/referral";
import Footer from "../../../layouts/footer";

const { Text, Link, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Step } = Steps;
const { TextArea } = Input;

const VoucherList = observer((props: any) => {
  const {
    vouchers,
    fetchVouchers,
    onCurrentBooking,
    addToCart,
    fetchUserVoucherDetail,
  } = useVoucher();
  const { user } = useAuth();
  const { navigateWithReferral } = useReferral();
  const navProps = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [paymentStatus, setPaymentStatus] = useState("success");
  const [rooms, setRooms] = useState([]);

  const [checkoutDate, setCheckoutDate] = useState("");

  const [roomQty, setRoomQty] = useState(1);
  const [searchDropdownVisible, setSearchDropdownVisible] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [searching, setSearching] = useState(false);
  const [payingNow, setPayingNow] = useState(false);
  const [loginAlertVisible, setLoginAlertVisible] = useState(false);
  const [filled, setFilled] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [priceType, setPriceType] = useState(0);
  const [specialRequest, setSpecialRequest] = useState("");
  const [checkedAddons, setCheckedAddons] = useState([]);
  const [checkedAddonDetails, setCheckedAddonDetails] = useState([]);
  const [openDatepicker, setOpenDatepicker] = useState(false);
  const [visibleDropdown, setVisibleDropdown] = useState(false);

  const [bookTableModalVisible, setBookTableModalVisible] = useState(false);
  const [voucherCodeInput, setVoucherCodeInput] = useState("");
  const [verifyingVoucher, setVerifyingVoucher] = useState(false);
  const [verifiedVouchers, setVerifiedVouchers] = useState<any[]>([]);
  const [verificationError, setVerificationError] = useState("");

  const [selectedRestaurant, setSelectedRestaurant] = useState<any>(null);
  const [adultPax, setAdultPax] = useState(2);
  const [childPax, setChildPax] = useState(0);
  const [checkinDate, setCheckinDate] = useState("");

  const [selectedVoucher, setSelectedVoucher] = useState<any>(null);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const handleOpen = (voucher: any) => {
    setSelectedVoucher(voucher);
    setSelectedPrice(null);
    setModalVisible(true);
  };

  const handleClose = () => {
    setModalVisible(false);
    setSelectedVoucher(null);
    setSelectedPrice(null);
  };

  useEffect(() => {
    onSearch();
  }, []);

  const handleProceed = () => {
    if (verifiedVouchers.length === 0) {
      setVerificationError("Please add at least one voucher");
      return;
    }

    // Check if all vouchers have the same voucher_title (type)
    const firstVoucherType = verifiedVouchers[0].voucher_title;
    const allSameType = verifiedVouchers.every(
      (voucher) => voucher.voucher_title === firstVoucherType
    );

    if (!allSameType) {
      const differentTypes = [
        ...new Set(verifiedVouchers.map((v) => v.voucher_title)),
      ];
      setVerificationError(
        `All vouchers must be of the same type. You have different types: ${differentTypes.join(
          ", "
        )}. Please ensure all vouchers are for the same dining experience.`
      );
      return;
    }

    // Check if all vouchers are from the same store
    const firstStore = verifiedVouchers[0].store_name;
    const allSameStore = verifiedVouchers.every(
      (voucher) => voucher.store_name === firstStore
    );

    if (!allSameStore) {
      setVerificationError(
        "All vouchers must be from the same restaurant. Please ensure all vouchers are valid for the same location."
      );
      return;
    }

    // If validation passes, proceed to the guest booking page
    setBookTableModalVisible(false);

    // Navigate to the new guest booking page with the verified vouchers
    navigateWithReferral(`/voucher/guest-booking`, {
      state: {
        verifiedVouchers,
        commonVoucherType: firstVoucherType,
        commonStore: firstStore,
      },
    });

    // Reset states
    setVerifiedVouchers([]);
    setVoucherCodeInput("");
    setVerificationError("");
  };

  // const handleSendOtp = async (email: string) => {
  //   if (!email) {
  //     message.error("Please enter your email address");
  //     return;
  //   }

  //   setSendingOtp(true);
  //   try {
  //     // Replace with your actual API call
  //     await axios.post("/api/send-otp", { email });
  //     setOtpSent(true);
  //     message.success("OTP sent to your email");
  //   } catch (error) {
  //     message.error("Failed to send OTP. Please try again.");
  //     console.error("OTP sending error:", error);
  //   } finally {
  //     setSendingOtp(false);
  //   }
  // };

  const handleRemoveVoucher = (index: number) => {
    setVerifiedVouchers((prev) => prev.filter((_, i) => i !== index));
  };

  // Add this function to handle voucher verification
  const handleVerifyVoucher = async () => {
    if (!voucherCodeInput.trim()) {
      setVerificationError("Please enter a voucher code");
      return;
    }

    // Check if voucher already exists in the list
    if (
      verifiedVouchers.some((v) => v.voucher_code === voucherCodeInput.trim())
    ) {
      setVerificationError("This voucher has already been added");
      return;
    }

    setVerifyingVoucher(true);
    setVerificationError("");

    try {
      const response = await fetchUserVoucherDetail(voucherCodeInput.trim());

      // Handle array response
      const voucherData = Array.isArray(response) ? response[0] : response;

      if (!voucherData) {
        setVerificationError("Voucher not found");
        return;
      }

      // Check if voucher is available (status not "used" and usedate is null)
      if (voucherData.status === "used" || voucherData.usedate) {
        setVerificationError("This voucher has already been used");
        return;
      }

      if (
        voucherData.status === "expired" ||
        moment().isAfter(voucherData.expired_date)
      ) {
        setVerificationError("This voucher has expired");
        return;
      }

      // Add to verified vouchers list with proper fields from API
      setVerifiedVouchers((prev) => [
        ...prev,
        {
          ...voucherData,
          voucher_code: voucherCodeInput.trim(),
          voucher_type: voucherData.voucher_title, // Using voucher_title as type identifier
          verified: true,
        },
      ]);

      setVoucherCodeInput(""); // Clear input after successful verification
    } catch (error) {
      setVerificationError("Invalid voucher code or voucher not found");
    } finally {
      setVerifyingVoucher(false);
    }
  };
  // const onSelectProduct = (product: any, selectedTable: any) => {
  //   onCurrentBooking({
  //     selectedProduct: product,
  //     bookingParams: {
  //       store_id: product.store_id,
  //       datereserve: checkinDate,
  //       timereserve: selectedTable,
  //       number_adult: adultPax,
  //       number_child: childPax,
  //       name,
  //       phone,
  //       email,
  //     },
  //   });
  //   console.log("check currentBooking ===> ", {
  //     selectedProduct: product,
  //     bookingParams: {
  //       store_id: product.store_id,
  //       datereserve: checkinDate,
  //       timereserve: selectedTable,
  //       number_adult: adultPax,
  //       number_child: childPax,
  //       name,
  //       phone,
  //       email,
  //     },
  //   });
  //   navigateWithReferral("checkout");
  //   // setSelectedRoom(product);
  //   // setCurrentStep(2);
  //   // setPriceType(priceType)
  //   onAutofillForm(true);
  // };

  const onOpenDatepicker = () => {
    console.log("onOpenDatepicker");
    setOpenDatepicker(true);
  };

  const onChangeDate = (date: any) => {
    console.log("onChangeDate");
    // console.log("Check date ===> ", date)
    setCheckinDate(date);
    // setCheckoutDate(date[1])
    setOpenDatepicker(false);
  };

  const onOpenChange = () => {
    console.log("onOpenChange");
    setOpenDatepicker(!openDatepicker);
  };

  const onSelectRestaurant = (value: any) => {
    setSelectedRestaurant(value);
  };

  const onVisibleDropdown = () => {
    setVisibleDropdown(!visibleDropdown);
  };

  const onAutofillForm = (checked: any) => {
    console.log("check onAutofillForm ===> ", checked);
    if (checked) {
      setFilled(checked);
      setName(
        `${user?.firstname !== undefined ? user?.firstname : ""}${
          user?.lastname !== undefined ? " " + user?.lastname : ""
        }`
      );
      setEmail(`${user?.email !== undefined ? user?.email : ""}`);
      setPhone(`${user?.phone !== undefined ? user?.phone : ""}`);
    } else {
      setFilled(checked);
      setName("");
      setEmail("");
      setPhone("");
    }
  };

  const onPayNow = () => {
    setPayingNow(true);
    const params = {
      UUID: user?.UUID,
      // RoomId: selectedRoom?.RoomId,
      Price_type: priceType,
      checkin_date: checkinDate,
      checkout_date: checkoutDate,
      adult_pax: adultPax,
      child_pax: childPax,
      room_qty: roomQty,
      Booking_Name: name,
      Booking_Email: email,
      Booking_Phone: phone,
      SpecialRequest: specialRequest,
    };
    const addonParams = [...checkedAddonDetails];
    mstBooking
      .createBooking(params, addonParams)
      .then((response) => {
        // createBooking api success
        mstBooking
          .createPayment({
            UUID: user?.UUID,
            order_number: response.order_number,
            paytype: 0,
          })
          .then((response) => {
            console.log(
              "Check response createPayment at home.js ===> ",
              response
            );
            // createPayment api success
            window.location.replace(response);
          })
          .catch((error) => {
            console.log("Check error createPayment at home.js ===> ", error);
            // createPayment api failed / error
            setPayingNow(false);
          });
      })
      .catch((error) => {
        console.log("Check response createBooking at home.js ===> ", error);
        // createBooking api failed / error
        setPayingNow(false);
      });
  };

  const onChangeQuantity = (type: any, action: any) => {
    const adultMax = 20; // up to 20 pax maximum
    const childMax = 20; // up to 20 pax maximum
    const roomMax = 20; // up to 20 pax maximum

    if (type === "adultPax") {
      if (action === "minus") {
        if (adultPax > 0) {
          setAdultPax(adultPax - 1);
        } else {
          console.log("Already reached minimum");
        }
      } else if (action === "add") {
        if (adultPax < adultMax) {
          setAdultPax(adultPax + 1);
        } else {
          console.log("Already reached maximum");
        }
      }
    } else if (type === "childPax") {
      if (action === "minus") {
        if (childPax > 0) {
          setChildPax(childPax - 1);
        } else {
          console.log("Already reached minimum");
        }
      } else if (action === "add") {
        if (childPax < childMax) {
          setChildPax(childPax + 1);
        } else {
          console.log("Already reached maximum");
        }
      }
    } else if (type === "roomQty") {
      if (action === "minus") {
        if (roomQty > 0) {
          setRoomQty(roomQty - 1);
        } else {
          console.log("Already reached minimum");
        }
      } else if (action === "add") {
        if (roomQty < roomMax) {
          setRoomQty(roomQty + 1);
        } else {
          console.log("Already reached maximum");
        }
      }
    }
  };

  const onSearch = async () => {
    // if (checkinDate == "") {
    //   onOpenDatepicker();
    // } else {
    // alert("Dear valued guest, please note that our reservation system can only acknowledge selection of only one room type for each reservation. Kindly create a new reservation if you wish to book for another room type. Thank you.")
    setSearching(true);
    await fetchVouchers({
      // @ts-ignore
      store_id: "",
      search: "",
    });
    setSearching(false);
    // .then((response: any) => {
    //   setSearching(false);
    //   setRooms(response);
    // })
    // .catch((error: any) => {
    //   setSearching(false);
    //   setRooms([]);
    //   alert(JSON.stringify(error));
    //   console.log(error);
    // });
    // }
  };

  const [restaurantListModalVisible, setRestaurantListModalVisible] =
    useState(false);
  // const [selectedRestaurant, setSelectedRestaurant] =
  // useState(false);
  const [restaurants, setRestaurants] = useState([]);

  const loadRestaurants = () => {
    mstGeneral
      .fetchRestaurants()
      .then((response: any) => {
        setRestaurants(response);
      })
      .catch((error: any) => {
        console.log(error);
      });
  };

  useEffect(() => {
    loadRestaurants();
  }, []);

  // const onOpenPriceSummaryModal = (item) => {
  //   console.log("check onOpenPriceSummaryModal ===> ", item);
  //   setPriceSummaryModalData(item);
  //   setPriceSummaryModalVisible(true);
  // };

  const menu = (
    <Menu style={{ padding: "1em" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1em",
        }}
      >
        <Text style={{ fontSize: 16 }}>Adult</Text>
        <div>
          <Button
            type="link"
            style={{
              fontSize: 20,
              marginRight: "1em",
              marginTop: 0,
              padding: "0em",
            }}
            onClick={(e) => {
              onChangeQuantity("adultPax", "minus");
            }}
          >
            -
          </Button>
          <Text style={{ fontSize: 16 }}>
            <b>{adultPax}</b>
          </Text>
          <Button
            type="link"
            style={{
              fontSize: 20,
              marginLeft: "1em",
              marginTop: 0,
              padding: "0em",
            }}
            onClick={(e) => {
              onChangeQuantity("adultPax", "add");
            }}
          >
            +
          </Button>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1em",
        }}
      >
        <Text style={{ fontSize: 16 }}>Child</Text>
        <div>
          <Button
            type="link"
            style={{
              fontSize: 20,
              marginRight: "1em",
              marginTop: 0,
              padding: "0em",
            }}
            onClick={(e) => {
              onChangeQuantity("childPax", "minus");
            }}
          >
            -
          </Button>
          <Text style={{ fontSize: 16 }}>
            <b>{childPax}</b>
          </Text>
          <Button
            type="link"
            style={{
              fontSize: 20,
              marginLeft: "1em",
              marginTop: 0,
              padding: "0em",
            }}
            onClick={(e) => {
              onChangeQuantity("childPax", "add");
            }}
          >
            +
          </Button>
        </div>
      </div>
      {/* <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', }}>
	  		<Text style={{ fontSize: 16 }}>Room</Text>
	  		<div>
	  			<Button type="link" style={{ fontSize: 20, marginRight: '1em', marginTop: 0, padding: '0em' }} onClick={(e) => { onChangeQuantity("roomQty", "minus") }}>-</Button>
	  			<Text style={{ fontSize: 16 }}><b>{roomQty}</b></Text>
	  			<Button type="link" style={{ fontSize: 20, marginLeft: '1em', marginTop: 0, padding: '0em' }} onClick={(e) => { onChangeQuantity("roomQty", "add") }}>+</Button>
	  		</div>
	  	</div> */}
    </Menu>
  );

  let prices = [];
  for (let i = 0; i < roomQty; i++) {
    prices.push({ value: i + 1 });
  }

  const renderStatus = (status: any) => {
    return status === "1" ? (
      <Tag color="green">Open</Tag>
    ) : (
      <Tag color="red">Closed</Tag>
    );
  };

  const onAddToCart = (voucher: any, selectedOption: any, quantity = 1) => {
    const newItem: any = {
      voucher_name: voucher?.title,
      voucher_id: voucher?.voucher_id,
      store_name: selectedRestaurant?.name,
      store_id: selectedRestaurant?.store_id,
      variant: {
        ...selectedOption,
        quantity: quantity,
      },
    };

    addToCart(newItem);

    console.log("onAddToCart product ===> ", newItem);
    // {
    //   store_name: 'R Loung',
    //   store_id: 1,
    //   products: [
    //     {
    //       title: 'Hi Tea',
    //       options: [
    //         {
    //           price_id: selectedPrice.price_id,
    //           title: selectedPrice.title,
    //           price: selectedPrice.amount
    //         },
    //       ] // this options will keep adding new data if user add another price option
    //     }
    //   ]
    // }
  };

  return (
    <div id="HomeScreen">
      <div className="container">
        <Navbar />
        <>
          {/* <div id="searchSection">
            <Title level={4}>Voucher Purchase</Title>
            <div style={{ width: "100%", marginBottom: 10 }}>
              <div style={{ marginBottom: 5 }}>Restaurant</div>
              <Button
                onClick={() => {
                  setRestaurantListModalVisible(true);
                }}
                size="large"
                style={{ width: "100%", textAlign: "left" }}
              >
                {selectedRestaurant?.name
                  ? selectedRestaurant?.name
                  : "Please select restaurant"}
              </Button>
            </div>

            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Button
                className="buttonSearch"
                loading={searching}
                style={{
                  width: "100%",
                  backgroundColor: "#e90056",
                  borderColor: "#e90056",
                }}
                size="large"
                type="primary"
                onClick={() => {
                  // @ts-ignore
                  onSearch();
                }}
              >
                
                <SearchOutlined />
                Search Voucher
              </Button>
            </div>
          </div> */}
          <div className="productListWrapper">
            {/* <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
	      	<Title level={4}>Select your room</Title>
	      	<Select size="large" defaultValue="1" style={{ marginRight: 10 }}>
			      <Option value="1">Sort By Newest</Option>
			      <Option value="2">Sort By Affordable Price</Option>
			      <Option value="3">Sort By Ratings</Option>
			    </Select>
	    	</div> */}
            <div style={{ marginBottom: 20 }}>
              <Alert
                style={{ paddingTop: 15, paddingBottom: 15 }}
                showIcon
                message="Already have voucher?"
                description="You can book your table now"
                type="info"
                action={
                  <Button
                    size="large"
                    type="primary"
                    onClick={() =>
                      navigateWithReferral("/voucher/guest-assign-slot")
                    }
                  >
                    Book Table
                  </Button>
                }
              />
            </div>

            <div>
              <Title level={4}>Available Vouchers</Title>
            </div>
            <Divider />
            <>
              <List
                grid={{
                  gutter: 16,
                  xs: 1, // 1 column on mobile
                  sm: 1, // 1 column on small tablets
                  md: 2, // 2 columns on medium screens
                  lg: 2, // 2 columns on large screens
                  xl: 2, // 2 columns on extra large screens
                  xxl: 2, // 2 columns on very large screens
                }}
                dataSource={vouchers}
                renderItem={(voucher: any) => (
                  <List.Item>
                    <Card
                      hoverable
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        width: "100%",
                      }}
                      bodyStyle={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        padding: "16px",
                      }}
                      cover={
                        <div
                          style={{
                            width: "100%",
                            aspectRatio: "16 / 9", // 16:9 aspect ratio
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#f5f5f5",
                          }}
                        >
                          <img
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              objectPosition: "center",
                            }}
                            src={voucher.image_url}
                            alt={voucher.title}
                            onError={(e) => {
                              e.currentTarget.src = "/placeholder-image.png";
                            }}
                          />
                        </div>
                      }
                      onClick={() => navigateWithReferral(voucher.voucher_id)}
                      actions={[
                        <Button
                          type="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigateWithReferral(voucher.voucher_id);
                          }}
                        >
                          Purchase
                        </Button>,
                      ]}
                    >
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Card.Meta
                          title={
                            <div
                              style={{
                                fontSize: "16px",
                                fontWeight: "bold",
                                lineHeight: "1.3",
                                minHeight: "20px",
                                maxHeight: "20px",
                                overflow: "hidden",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                marginBottom: "8px",
                              }}
                            >
                              {voucher.title}
                            </div>
                          }
                          description={
                            <div
                              style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              {/* Description */}
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#666",
                                  lineHeight: "1.4",
                                  minHeight: "34px",
                                  maxHeight: "34px",
                                  overflow: "hidden",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  marginBottom: "12px",
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: voucher.description
                                    ? voucher.description
                                        .replace(/<[^>]+>/g, "")
                                        .substring(0, 100) + "..."
                                    : "No description available",
                                }}
                              />

                              {/* Price Tags */}
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                {voucher.price_cat &&
                                voucher.price_cat.length > 0 ? (
                                  voucher.price_cat.map((prc: any) => (
                                    <Tag color="green" key={prc.price_id}>
                                      {`${prc.title} (RM ${prc.amount})`}
                                    </Tag>
                                  ))
                                ) : (
                                  <Tag color="green">{`RM ${voucher.amount}`}</Tag>
                                )}
                              </div>
                            </div>
                          }
                        />
                      </div>
                    </Card>
                  </List.Item>
                )}
              />
            </>
          </div>
          {/* @ts-ignore */}
          <Modal
            title="Restaurant List"
            visible={restaurantListModalVisible}
            footer={null}
            onCancel={() => {
              setBookTableModalVisible(false);
              setVerifiedVouchers([]);
              setVoucherCodeInput("");
              setVerificationError("");
              setVerifyingVoucher(false);
            }}
          >
            <List
              itemLayout="horizontal"
              dataSource={restaurants}
              renderItem={(rest: any) => (
                <List.Item
                  onClick={() => {
                    if (rest.status === "0") {
                      alert(
                        "Restaurant is closed. Please try again or select another option. Thank you!"
                      );
                      return;
                    }
                    onSelectRestaurant(rest);
                    setRestaurantListModalVisible(false);
                  }}
                  style={{
                    cursor: "pointer",
                    borderBottom: "1px solid #f0f0f0",
                    padding: "12px 0",
                  }}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        shape="square"
                        size={64}
                        src={rest.logo_url || rest.images}
                      />
                    }
                    title={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span>{rest.name}</span>
                        {renderStatus(rest.status)}
                      </div>
                    }
                    description={
                      <div>
                        <div>{rest.address}</div>
                        <div>{rest.phone_number}</div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Modal>
          {/* @ts-ignore */}
          <Modal
            title="Book Table with Voucher"
            visible={bookTableModalVisible}
            onCancel={() => {
              setBookTableModalVisible(false);
              setVerifiedVouchers([]);
              setVoucherCodeInput("");
              setVerificationError("");
            }}
            footer={null}
            width={600}
            centered
          >
            <div style={{ marginBottom: 16 }}>
              <Text
                strong
                style={{
                  fontSize: "16px",
                  marginBottom: "8px",
                  display: "block",
                }}
              >
                Add Your Vouchers
              </Text>

              {/* Voucher Input Section */}
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "flex-start",
                  marginBottom: "8px",
                }}
              >
                <Input
                  placeholder="Enter voucher code (e.g., HTB2025111280168490)"
                  value={voucherCodeInput}
                  onChange={(e) => {
                    setVoucherCodeInput(e.target.value.toUpperCase());
                    setVerificationError("");
                  }}
                  onPressEnter={handleVerifyVoucher}
                  size="large"
                  disabled={verifyingVoucher}
                  style={{ flex: 1 }}
                  allowClear
                />
                <Button
                  type="primary"
                  onClick={handleVerifyVoucher}
                  loading={verifyingVoucher}
                  size="large"
                  style={{ minWidth: "100px" }}
                >
                  {verifyingVoucher ? "Verifying..." : "Add Voucher"}
                </Button>
              </div>

              {verificationError && (
                <Alert
                  message={verificationError}
                  type="error"
                  showIcon
                  style={{ marginTop: 8 }}
                  closable
                  onClose={() => setVerificationError("")}
                />
              )}
            </div>

            {/* Verified Vouchers List */}
            {verifiedVouchers.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "12px",
                  }}
                >
                  <Text strong style={{ fontSize: "16px" }}>
                    Verified Vouchers ({verifiedVouchers.length})
                  </Text>
                  <Button
                    type="link"
                    danger
                    onClick={() => setVerifiedVouchers([])}
                    size="small"
                  >
                    Clear All
                  </Button>
                </div>

                <div
                  style={{
                    maxHeight: 300,
                    overflow: "auto",
                    border: "1px solid #d9d9d9",
                    borderRadius: "6px",
                    padding: "12px",
                  }}
                >
                  {verifiedVouchers.map((voucher, index) => (
                    <Card
                      key={voucher.voucher_code}
                      size="small"
                      style={{
                        marginBottom: "12px",
                        border: "1px solid #f0f0f0",
                      }}
                      bodyStyle={{ padding: "12px" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: "8px",
                            }}
                          >
                            <Text
                              strong
                              style={{ fontSize: "14px", marginRight: "8px" }}
                            >
                              {voucher.voucher_title}
                            </Text>
                            <Tag color="green">Verified</Tag>
                          </div>

                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "8px",
                              fontSize: "12px",
                            }}
                          >
                            <div>
                              <Text type="secondary">Code:</Text>
                              <br />
                              <Text strong>{voucher.voucher_code}</Text>
                            </div>
                            <div>
                              <Text type="secondary">Type:</Text>
                              <br />
                              <Tag color="blue">{voucher.price_type}</Tag>
                            </div>
                            <div>
                              <Text type="secondary">Restaurant:</Text>
                              <br />
                              <Text strong>{voucher.store_name}</Text>
                            </div>
                            <div>
                              <Text type="secondary">Expires:</Text>
                              <br />
                              <Text>
                                {moment(voucher.expired_date).format(
                                  "DD MMM YYYY"
                                )}
                              </Text>
                            </div>
                          </div>
                        </div>

                        <Button
                          type="text"
                          danger
                          size="small"
                          onClick={() => handleRemoveVoucher(index)}
                          style={{ marginLeft: "12px" }}
                        >
                          Remove
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>

                {/* Voucher Type Validation Display */}
                {verifiedVouchers.length > 1 && (
                  <div style={{ marginTop: "12px" }}>
                    <Alert
                      message={`Voucher Type: ${verifiedVouchers[0].voucher_title}`}
                      description={`All ${verifiedVouchers.length} vouchers must be of the same type`}
                      type="info"
                      showIcon
                    />
                  </div>
                )}
              </div>
            )}

            {/* Proceed Button */}
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <Button
                type="primary"
                size="large"
                onClick={handleProceed}
                disabled={verifiedVouchers.length === 0}
                style={{
                  minWidth: 200,
                  height: "48px",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                Proceed to Book Table ({verifiedVouchers.length})
              </Button>
            </div>

            <div style={{ marginTop: 16 }}>
              <Alert
                message="Important Notes"
                description={
                  <ul style={{ margin: 0, paddingLeft: "16px" }}>
                    <li>Add all vouchers you want to use for this booking</li>
                    <li>All vouchers must be for the same dining experience</li>
                    <li>
                      You can remove vouchers by clicking the Remove button
                    </li>
                    <li>
                      Vouchers will be verified for availability and validity
                    </li>
                  </ul>
                }
                type="info"
                showIcon
              />
            </div>
          </Modal>
        </>
        <Footer />
      </div>
    </div>
  );
});

export default VoucherList;
